import React, { useState, useCallback, useMemo } from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import { Eye } from 'lucide-react';

import useBenefitAuditApi from './config';
import { getBenefitAuditLog } from '../../services/benefitReportService';
import { parseTableParams, formatDateTime } from '../../utils/Utils';
import DataTable from '../../components/ui/DataTable';
import Button from '../../components/ui/Button';

const Container = styled.div``;

const IconButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  border-radius: 6px;
  border: none;
  background: transparent;
  color: #6b7280;
  cursor: pointer;
  transition: background 0.15s, color 0.15s;
  &:hover {
    background: #f1f5f9;
    color: ${({ theme }) => theme.colors.text.primary};
  }
`;

const Modal = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(0,0,0,0.4);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
`;

const ModalContent = styled.div`
  background: white;
  border-radius: 8px;
  padding: 24px;
  width: 90%;
  max-width: 720px;
  max-height: 80vh;
  overflow-y: auto;
`;

const ModalTitle = styled.h3`
  margin: 0 0 16px;
  color: #004242;
  font-family: 'Nunito Sans', sans-serif;
`;

const FieldRow = styled.div`
  display: grid;
  grid-template-columns: 180px 1fr;
  gap: 8px;
  padding: 6px 0;
  border-bottom: 1px solid #f1f5f9;
  font-size: 13px;
  &:last-of-type { border-bottom: none; }
`;

const FieldLabel = styled.div`color: #64748b; font-weight: 600;`;
const FieldValue = styled.div`color: #004242; word-break: break-word;`;

const PayloadBox = styled.pre`
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  border-radius: 4px;
  padding: 12px;
  font-size: 12px;
  font-family: 'SFMono-Regular', Consolas, 'Liberation Mono', Menlo, monospace;
  white-space: pre-wrap;
  word-break: break-word;
  max-height: 280px;
  overflow-y: auto;
  margin: 8px 0 0;
`;

const ActionsCell = ({ row, onView }) => (
  <IconButton type="button" onClick={() => onView(row)} aria-label="view">
    <Eye size={16} />
  </IconButton>
);

export default function BenefitAuditLogsPage() {
  const { t, i18n } = useTranslation();
  const lang = i18n.language;

  const [queryParams, setQueryParams] = useState({ pageSize: 50, pageNumber: 1 });
  const [filterList, setFilterList] = useState([]);
  const [selectedDetail, setSelectedDetail] = useState(null);

  const { columns: baseColumns, data, fetching, pagination } = useBenefitAuditApi(queryParams);

  const handleView = useCallback(async (row) => {
    const res = await getBenefitAuditLog(row.id);
    if (res.data?.status) setSelectedDetail(res.data.data);
  }, []);

  const columns = useMemo(() => [
    ...baseColumns,
    {
      name: 'actions',
      label: ' ',
      options: {
        filter: false,
        sort: false,
        empty: true,
        customBodyRenderLite: (dataIndex) => {
          const row = data[dataIndex];
          return row ? <ActionsCell row={row} onView={handleView} /> : null;
        },
      },
    },
  ], [baseColumns, data, handleView]);

  const handleDataStateChange = useCallback((_, tableState) => {
    setFilterList(tableState.filterList);
    const params = parseTableParams(tableState, columns);
    setQueryParams(params);
  }, [columns]);

  const handleFilterConfirm = useCallback((newFilterList) => {
    setFilterList(newFilterList);
  }, []);

  return (
    <Container>

      <DataTable
        title={t('menu.benefitAuditLogs')}
        fetching={fetching}
        columns={columns}
        data={data}
        totalRowCount={pagination.totalRowCount}
        pageIndex={pagination.pageIndex}
        pageSize={pagination.pageSize}
        filterList={filterList}
        onFilterConfirm={handleFilterConfirm}
        onTableChanged={handleDataStateChange}
      />

      {selectedDetail && (
        <Modal onClick={() => setSelectedDetail(null)}>
          <ModalContent onClick={(e) => e.stopPropagation()}>
            <ModalTitle>{t('benefitAuditLog.detail')}</ModalTitle>

            <FieldRow><FieldLabel>{t('benefitAuditLog.timestamp')}</FieldLabel><FieldValue>{formatDateTime(selectedDetail.timestamp, lang)}</FieldValue></FieldRow>
            <FieldRow><FieldLabel>{t('benefitAuditLog.entityType')}</FieldLabel><FieldValue>{selectedDetail.entityType}</FieldValue></FieldRow>
            <FieldRow><FieldLabel>{t('benefitAuditLog.entityId')}</FieldLabel><FieldValue>{selectedDetail.entityId}</FieldValue></FieldRow>
            <FieldRow><FieldLabel>{t('benefitAuditLog.action')}</FieldLabel><FieldValue>{selectedDetail.action}</FieldValue></FieldRow>
            <FieldRow><FieldLabel>{t('benefitAuditLog.actor')}</FieldLabel><FieldValue>{selectedDetail.actorName || selectedDetail.actorUserId}</FieldValue></FieldRow>
            <FieldRow><FieldLabel>{t('benefitAuditLog.targetEmployee')}</FieldLabel><FieldValue>{selectedDetail.employeeName || '-'}</FieldValue></FieldRow>
            <FieldRow><FieldLabel>{t('benefitAuditLog.tag')}</FieldLabel><FieldValue>{selectedDetail.tag || '-'}</FieldValue></FieldRow>
            <FieldRow><FieldLabel>{t('benefitAuditLog.ipAddress')}</FieldLabel><FieldValue>{selectedDetail.ipAddress || '-'}</FieldValue></FieldRow>

            {selectedDetail.payloadJson && (
              <>
                <FieldRow style={{ marginTop: 12 }}>
                  <FieldLabel>{t('benefitAuditLog.payload')}</FieldLabel>
                  <FieldValue style={{ color: '#94a3b8', fontStyle: 'italic' }}>
                    {t('benefitAuditLog.payloadKvkkNote')}
                  </FieldValue>
                </FieldRow>
                <PayloadBox>{(() => {
                  try { return JSON.stringify(JSON.parse(selectedDetail.payloadJson), null, 2); }
                  catch { return selectedDetail.payloadJson; }
                })()}</PayloadBox>
              </>
            )}

            <div style={{ marginTop: 20, textAlign: 'right' }}>
              <Button type="button" onClick={() => setSelectedDetail(null)}>{t('common.close')}</Button>
            </div>
          </ModalContent>
        </Modal>
      )}
    </Container>
  );
}
